import { useEffect, useState, useCallback, useRef } from 'react'
import type {
  MessageUpdateReason,
  SendMediaOptions,
} from '@twilio/conversations'
import {
  getConversationsList,
  postConversations,
  postConversationsToken,
  postConversationsTokenRefresh,
  getConversationsHistory,
  markConversationRead,
  postPresenceEnsure,
  postConversationReopen,
  getConversationById,
  estaEncerrada,
} from 'services/conversations/conversationsApi'
import type { ConversationAuthPair } from 'utils/conversationUserAuthHeader'
import { effectiveFileMime, isChatAudioMime } from 'utils/chatAttachmentLimits'
import {
  ConversationsApiError,
  DEFAULT_RETRY_AFTER_SECONDS,
  describeConversationsError,
  toConversationsApiError,
} from 'services/conversations/conversationsApiError'
import { acharConversaDoPar } from 'services/conversations/acharConversaDoPar'
import { runHistoryShadowRead } from 'services/conversations/historyShadowRead'
import {
  historyMessageHasContent,
  mapHistoryMessage,
  mergeBySid,
} from 'services/conversations/historyAsSource'
import { useChatHistoryShadowRead } from './useChatHistoryShadowRead'
import { useChatHistoryAsSource } from './useChatHistoryAsSource'
import { useChatPresenceEnsure } from './useChatPresenceEnsure'
import { useConversationCloseFlags } from './useConversationCloseFlags'
import {
  diagnosePresence403,
  exigeReopen,
  podeTentarReabrir,
  presenceMessageFor,
  type PresenceDiagnosis,
} from 'services/conversations/presenceDiagnosis'
import { normalizeChatAudioFileIfNeeded } from 'utils/convertChatAudioForSend'
import { alertDanger } from 'utils/toast'
import chatAnalytics from 'services/analytics/Chat/chatAnalytics'

function fileToSendMediaOptions(file: File): SendMediaOptions {
  const filename = (file.name && file.name.trim()) || 'anexo'
  const contentType = effectiveFileMime(file) || 'application/octet-stream'
  return { media: file, contentType, filename }
}

async function prepareFileForTwilioSend(file: File): Promise<File> {
  const mime = effectiveFileMime(file)
  if (isChatAudioMime(mime)) {
    return normalizeChatAudioFileIfNeeded(file)
  }
  return file
}

async function safeAdvanceLastReadMessageIndex(
  conversation: {
    advanceLastReadMessageIndex?: (n: number) => Promise<unknown>
  },
  index: unknown
) {
  if (typeof index !== 'number' || Number.isNaN(index)) return
  if (typeof conversation.advanceLastReadMessageIndex !== 'function') return
  try {
    await conversation.advanceLastReadMessageIndex(index)
  } catch (e) {
    console.warn('[TwilioChat] advanceLastReadMessageIndex', e)
  }
}

function formatChatSendError(_error: unknown, kind: 'text' | 'media'): string {
  return kind === 'media'
    ? 'Não foi possível enviar o arquivo. Tente novamente.'
    : 'Não foi possível enviar a mensagem. Tente novamente.'
}

type ConnectionState =
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'disconnected'
  | 'denied'

export interface ServiceRoomChatMessage {
  sid: string
  body: string
  author: string
  timestamp: Date
  /** Índice do Twilio (para `advanceLastReadMessageIndex`); não confundir com posição no array. */
  twilioMessageIndex?: number
  attributes?: {
    fileUrl?: string
    fileName?: string
    fileSize?: number
    contentType?: string
    deleted?: boolean
    isDeleted?: boolean
  }
}

function parseMessageAttributes(
  raw: unknown
): ServiceRoomChatMessage['attributes'] {
  if (raw == null || raw === '') return undefined
  if (typeof raw === 'object') {
    return raw as ServiceRoomChatMessage['attributes']
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as ServiceRoomChatMessage['attributes']
    } catch {
      return undefined
    }
  }
  return undefined
}

function coerceMessageDate(t: unknown): Date {
  if (t instanceof Date && !isNaN(t.getTime())) {
    return t
  }
  if (t != null) {
    const d = new Date(t as string | number)
    if (!isNaN(d.getTime())) {
      return d
    }
  }
  return new Date(0)
}

/** Twilio v3: preferir `dateCreated`; `timestamp` é legado / opcional no payload. */
function twilioMessageTime(m: {
  dateCreated?: Date | null
  timestamp?: unknown
}): Date {
  return coerceMessageDate(m.dateCreated ?? m.timestamp)
}

function collectMediaFromMessage(m: any): any[] {
  if (Array.isArray(m?.attachedMedia) && m.attachedMedia.length > 0) {
    return [...m.attachedMedia]
  }
  if (m?.media != null) {
    return [m.media]
  }
  return []
}

/** Ignora “fantasmas” (corpo vazio, sem mídia) e reduz eventos duplicados com o send(). */
function isDeletedTwilioMessage(m: { attributes?: unknown }): boolean {
  const attrs = parseMessageAttributes(m.attributes)
  if (attrs?.deleted === true || attrs?.isDeleted === true) {
    return true
  }
  return false
}

function hasRenderableTwilioContent(m: {
  body?: string | null
  type?: string
  attachedMedia?: unknown[] | null
  media?: unknown | null
  attributes?: unknown
}): boolean {
  if (isDeletedTwilioMessage(m)) {
    return false
  }
  const text = m.body != null ? String(m.body).trim() : ''
  if (text.length > 0) {
    return true
  }
  if (Array.isArray(m.attachedMedia) && m.attachedMedia.length > 0) {
    return true
  }
  if (m.media != null) {
    return true
  }
  if (m.type === 'media') {
    return true
  }
  return false
}

function mapTwilioMessage(m: any): ServiceRoomChatMessage {
  const baseAttrs = parseMessageAttributes(m.attributes)
  const medias = collectMediaFromMessage(m)
  let body = m.body != null ? String(m.body) : ''
  if (!body.trim() && medias.length > 0) {
    const first = medias[0]
    const name =
      (typeof first?.filename === 'string' && first.filename) ||
      (baseAttrs?.fileName as string | undefined) ||
      'Anexo'
    const sizeFromMedia =
      typeof first?.size === 'number' && !Number.isNaN(first.size)
        ? first.size
        : undefined
    const contentTypeFromMedia =
      typeof first?.contentType === 'string' && first.contentType
        ? first.contentType
        : undefined
    body = `📎 ${name}`
    return {
      sid: m.sid,
      body,
      author: m.author ?? '',
      timestamp: twilioMessageTime(m),
      twilioMessageIndex:
        typeof m.index === 'number' && !Number.isNaN(m.index)
          ? m.index
          : undefined,
      attributes: {
        ...baseAttrs,
        fileName:
          (typeof first?.filename === 'string' && first.filename) ||
          baseAttrs?.fileName,
        fileSize: sizeFromMedia ?? baseAttrs?.fileSize,
        contentType: contentTypeFromMedia ?? baseAttrs?.contentType,
      },
    }
  }
  return {
    sid: m.sid,
    body,
    author: m.author ?? '',
    timestamp: twilioMessageTime(m),
    twilioMessageIndex:
      typeof m.index === 'number' && !Number.isNaN(m.index)
        ? m.index
        : undefined,
    attributes: baseAttrs,
  }
}

/** URL pré-assinada (expira); sempre preferir renovar via SDK antes de exibir/baixar. */
async function fetchTwilioMessageMediaUrl(
  twilioMessage: any
): Promise<string | null> {
  const medias = collectMediaFromMessage(twilioMessage)
  if (medias.length === 0) {
    return null
  }
  try {
    if (
      typeof twilioMessage.getTemporaryContentUrlsForAttachedMedia ===
      'function'
    ) {
      const urlMap =
        await twilioMessage.getTemporaryContentUrlsForAttachedMedia()
      if (urlMap && urlMap.size > 0) {
        const fileUrl = urlMap.values().next().value
        if (typeof fileUrl === 'string' && fileUrl) {
          return fileUrl
        }
      }
    }
    if (typeof medias[0]?.getContentTemporaryUrl === 'function') {
      const fileUrl = await medias[0].getContentTemporaryUrl()
      if (typeof fileUrl === 'string' && fileUrl) {
        return fileUrl
      }
    }
  } catch (e) {
    console.debug('[TwilioChat] media URL not available', e)
  }
  return null
}

async function enrichMessageWithMediaDownloadUrl(
  twilioMessage: any,
  base: ServiceRoomChatMessage
): Promise<ServiceRoomChatMessage> {
  const medias = collectMediaFromMessage(twilioMessage)
  if (medias.length === 0) {
    return base
  }
  const fresh = await fetchTwilioMessageMediaUrl(twilioMessage)
  if (fresh) {
    return { ...base, attributes: { ...base.attributes, fileUrl: fresh } }
  }
  return base
}

/** Exibição: thread cronológica crescente (antigas no topo, conversa atual / última mensagem no fim). */
function sortMessagesByConversationOrder(
  list: ServiceRoomChatMessage[]
): ServiceRoomChatMessage[] {
  return [...list].sort((a, b) => {
    const ta = a.timestamp.getTime()
    const tb = b.timestamp.getTime()
    if (ta !== tb) {
      return ta - tb
    }
    const ia = a.twilioMessageIndex
    const ib = b.twilioMessageIndex
    if (ia != null && ib != null && ia !== ib) {
      return ia - ib
    }
    if (ia != null && ib == null) {
      return -1
    }
    if (ia == null && ib != null) {
      return 1
    }
    return a.sid.localeCompare(b.sid)
  })
}

function isProfessionalAuthor(
  author: string | null | undefined,
  professionalIdStr: string | undefined
): boolean {
  if (!author) {
    return false
  }
  if (professionalIdStr && author.includes(professionalIdStr)) {
    return true
  }
  return author.includes('professional:')
}

function isPatientAuthor(
  author: string | null | undefined,
  personIdStr: string | undefined
): boolean {
  if (!author) {
    return false
  }
  if (personIdStr && author.includes(personIdStr)) {
    return true
  }
  return author.includes('patient:')
}

/** Só incrementa contador de não lidas se a mensagem não for da identidade deste cliente (médico vs paciente). */
function isIncomingUnreadForViewer(
  author: string | null | undefined,
  twilioAs: 'patient' | 'professional',
  personIdStr: string | undefined,
  professionalIdStr: string | undefined
): boolean {
  if (!author) {
    return false
  }
  if (twilioAs === 'professional') {
    return !isProfessionalAuthor(author, professionalIdStr)
  }
  return !isPatientAuthor(author, personIdStr)
}

export type UseTwilioServiceRoomChatOptions = {
  personId: string | undefined
  professionalId: string | undefined
  twilioAs?: 'patient' | 'professional'
  twilioConversationSid?: string
  patientDisplayName?: string
  professionalDisplayName?: string
  enabled?: boolean
  appointmentId?: string
}

/**
 * `unavailable` é separado de `error` porque a reação é oposta: erro é
 * inesperado e o usuário pode tentar de novo; indisponibilidade é o servidor
 * dizendo que não há capacidade agora, e insistir foi o que derrubou a rota.
 *
 * `readOnly` é o estado do modelo de presença por demanda: o profissional
 * perdeu a participação na conversa, o histórico continua legível, mas a
 * escrita está fechada até reabrir.
 */
/** Paginador do SDK: `prevPage()` devolve outro paginador, daí a recursão. */
interface MessagePaginator {
  items: unknown[]
  hasPrevPage: boolean
  prevPage: () => Promise<MessagePaginator>
}

type ChatStatus =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'readOnly'
  | 'unavailable'
  | 'error'

export interface UseTwilioServiceRoomChatReturn {
  /** Fase 4: motivo de a escrita estar fechada; `null` quando liberada. */
  presenceMessage: string | null
  /** `false` quando reabrir não resolveria (ex.: namespace divergente). */
  canReopen: boolean
  reopenConversation: () => Promise<void>
  /** Re-executa a inicialização do chat (ex.: conversa reaberta por fora). */
  reinitialize: () => void
  reopening: boolean
  /** Janela do `Retry-After` quando o servidor sinaliza falta de capacidade. */
  retryAfterSeconds: number | null
  status: ChatStatus
  connectionState: ConnectionState | null
  errorMessage: string | null
  messages: ServiceRoomChatMessage[]
  sendText: (text: string, files?: File[]) => Promise<void>
  sendMedia: (file: File) => Promise<void>
  /** Envia vários anexos (1 mensagem Twilio por arquivo). Legenda só no primeiro. */
  sendAttachments: (files: File[], caption?: string) => Promise<void>
  sending: boolean
  typingParticipants: string[]
  notifyTyping: () => void
  markAsRead: () => Promise<void>
  unreadCount: number
  hasMoreMessages: boolean
  loadingMore: boolean
  loadMoreMessages: () => Promise<void>
  /** Nova URL temporária para download/visualização (evita AccessDenied por URL expirada). */
  getFreshMediaDownloadUrl: (messageSid: string) => Promise<string | null>
  deleteMessage: (messageSid: string) => Promise<void>
  deletingMessageSid: string | null
}

export function useTwilioServiceRoomChat(
  options: UseTwilioServiceRoomChatOptions
): UseTwilioServiceRoomChatReturn {
  const {
    personId,
    professionalId,
    twilioAs = 'patient',
    twilioConversationSid: initialConversationSid,
    enabled = true,
  } = options

  /** Mesmo par que o portal do paciente (evita conversa duplicada por number vs string no JSON do header). */
  const personIdStr =
    personId != null && String(personId).trim() !== ''
      ? String(personId).trim()
      : undefined
  const professionalIdStr =
    professionalId != null && String(professionalId).trim() !== ''
      ? String(professionalId).trim()
      : undefined

  const shadowReadEnabled = useChatHistoryShadowRead()
  const historyAsSource = useChatHistoryAsSource()
  /** Cursor do `/history` — só usado quando ele é a fonte de leitura (Fase 3). */
  const historyCursorRef = useRef<string | null>(null)
  /** UUID interno da conversa. O `POST /:id/read` usa ele, não o sid do Twilio. */
  const conversationInternalIdRef = useRef<string | null>(null)
  const presenceEnsureEnabled = useChatPresenceEnsure()
  const closeFlags = useConversationCloseFlags()
  /**
   * O effect principal não tem `closeFlags` nas deps (recriaria o Client
   * Twilio), então ler o objeto direto congela as flags do render em que ele
   * rodou — e o Remote Config costuma resolver depois. O ref lê o valor atual.
   */
  const closeFlagsRef = useRef(closeFlags)
  closeFlagsRef.current = closeFlags
  /** Incrementar re-executa o `initializeChat` (ex.: após reabrir sem Client). */
  const [reinitKey, setReinitKey] = useState(0)
  const reinitialize = useCallback(() => setReinitKey((k) => k + 1), [])
  /** Participantes da conversa — base para distinguir as duas causas de 403. */
  const conversationParticipantsRef = useRef<
    Array<{ identity: string; sid: string }>
  >([])
  const tokenIdentityRef = useRef<string | undefined>(undefined)
  const [presenceMessage, setPresenceMessage] = useState<string | null>(null)
  const [canReopen, setCanReopen] = useState(false)
  const [reopening, setReopening] = useState(false)
  /** `true` quando a última falha foi 409: reabrir exige `reopen` antes. */
  const precisaReopenRef = useRef(false)
  const shadowReadDoneRef = useRef(false)
  const shadowReadPairKey = `${personId ?? ''}:${professionalId ?? ''}`
  const shadowReadPairKeyRef = useRef(shadowReadPairKey)
  if (shadowReadPairKeyRef.current !== shadowReadPairKey) {
    // Outra conversa: mede de novo.
    shadowReadPairKeyRef.current = shadowReadPairKey
    shadowReadDoneRef.current = false
  }

  const [status, setStatus] = useState<ChatStatus>('idle')
  /** Janela informada pelo servidor no `Retry-After` quando falta capacidade. */
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null
  )
  const [connectionState, setConnectionState] =
    useState<ConnectionState | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [messages, setMessages] = useState<ServiceRoomChatMessage[]>([])
  const [sending, setSending] = useState(false)
  const [typingParticipants, setTypingParticipants] = useState<string[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  /**
   * Paginador do SDK. Precisa ser guardado: `getMessages()` devolve sempre a
   * página mais recente, e só `prevPage()` anda para trás no histórico.
   */
  const paginatorRef = useRef<MessagePaginator | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [deletingMessageSid, setDeletingMessageSid] = useState<string | null>(
    null
  )

  const clientRef = useRef<any>(null)
  const conversationRef = useRef<any>(null)
  const seenSidsRef = useRef<Set<string>>(new Set())
  const clientIterationRef = useRef(0)
  /** Evita dois `send` no mesmo tick (duplo clique/Enter) — `sending` em state não basta. */
  const sendInFlightRef = useRef(false)
  const twilioMessageBySidRef = useRef<Map<string, any>>(new Map())

  const rememberTwilioMessage = useCallback((m: any) => {
    if (m && typeof m.sid === 'string' && m.sid.length > 0) {
      twilioMessageBySidRef.current.set(m.sid, m)
    }
  }, [])

  const dropMessageFromChatState = useCallback((sid: string) => {
    if (!sid) return
    setMessages((prev) => prev.filter((m) => m.sid !== sid))
    seenSidsRef.current.delete(sid)
    twilioMessageBySidRef.current.delete(sid)
  }, [])

  const getFreshMediaDownloadUrl = useCallback(async (messageSid: string) => {
    const m = twilioMessageBySidRef.current.get(messageSid)
    if (!m) {
      return null
    }
    return fetchTwilioMessageMediaUrl(m)
  }, [])

  const authPair: ConversationAuthPair | null =
    personIdStr && professionalIdStr
      ? { personId: personIdStr, professionalId: professionalIdStr }
      : null

  const loadMoreMessages = useCallback(async () => {
    if (!conversationRef.current || loadingMore || !hasMoreMessages) return

    // Regressão: aqui havia `getMessages(30)`, que devolve de novo a página mais
    // recente. Todos os sids já estavam vistos, nada era acrescentado e
    // `hasMoreMessages` continuava true — o botão carregava para sempre sem
    // trazer nada. O histórico anterior só vem por `prevPage()`.
    // Fase 3: com o `/history` como fonte, "mais antigas" anda por cursor —
    // o paginator do SDK nem chega a existir nesse caminho.
    if (historyAsSource && authPair) {
      const cursor = historyCursorRef.current
      if (!cursor) {
        setHasMoreMessages(false)
        return
      }
      setLoadingMore(true)
      try {
        const page = await getConversationsHistory(
          {
            personId: authPair.personId,
            professionalId: authPair.professionalId,
            cursor,
            limit: 30,
            order: 'desc',
          },
          authPair
        )
        const novas = page.items
          .filter(historyMessageHasContent)
          .map(mapHistoryMessage)
        novas.forEach((m) => seenSidsRef.current.add(m.sid))
        setMessages((prev) => mergeBySid(prev, novas) as never)
        historyCursorRef.current = page.nextCursor ?? null
        setHasMoreMessages(Boolean(page.nextCursor))
      } catch (error) {
        console.error('Error loading more messages (history):', error)
      } finally {
        setLoadingMore(false)
      }
      return
    }

    const current = paginatorRef.current
    if (!current || !current.hasPrevPage) {
      setHasMoreMessages(false)
      return
    }

    setLoadingMore(true)
    try {
      const paginator = await current.prevPage()
      paginatorRef.current = paginator
      if (paginator && paginator.items.length > 0) {
        const rawRaws = paginator.items.filter(
          (m: any) =>
            !seenSidsRef.current.has(m.sid) && hasRenderableTwilioContent(m)
        )
        const newMessages = await Promise.all(
          rawRaws.map(async (m: any) => {
            rememberTwilioMessage(m)
            const mapped = mapTwilioMessage(m)
            return enrichMessageWithMediaDownloadUrl(m, mapped)
          })
        )

        setMessages((prev) =>
          sortMessagesByConversationOrder([...newMessages, ...prev])
        )
        newMessages.forEach((m: any) => seenSidsRef.current.add(m.sid))
      }
      // Fora do `if`: página só com mensagens já vistas ou sem conteúdo
      // renderizável ainda precisa mover o "tem mais".
      setHasMoreMessages(!!paginator.hasPrevPage)
    } catch (error) {
      console.error('Error loading more messages:', error)
    } finally {
      setLoadingMore(false)
    }
  }, [
    loadingMore,
    hasMoreMessages,
    rememberTwilioMessage,
    historyAsSource,
    authPair,
  ])

  /**
   * Fase 2 — shadow read do `/history`.
   *
   * Efeito separado de propósito: a flag vem do Remote Config **depois** da
   * montagem, e colocá-la nas dependências do efeito de conexão faria o cliente
   * Twilio reconectar só porque um booleano chegou.
   *
   * Roda uma vez por conversa, sem `await` no caminho da UI: é medição e não
   * pode atrasar nem derrubar o chat que o profissional está usando.
   */
  useEffect(() => {
    if (!shadowReadEnabled) return
    if (status !== 'ready' && status !== 'readOnly') return
    if (!authPair) return
    if (shadowReadDoneRef.current) return

    shadowReadDoneRef.current = true
    let cancelled = false
    const sdkSids = Array.from(seenSidsRef.current)

    void runHistoryShadowRead(sdkSids, authPair).then((result) => {
      if (cancelled) return
      chatAnalytics.trackHistoryShadowRead({
        appointmentId: options.appointmentId,
        status: result.status,
        ...(result.divergence ?? {}),
        // Sem isto, `failed` chegava ao Analytics sem nenhuma causa e não dava
        // para separar indisponibilidade de erro de contrato.
        ...(result.errorStatus ? { errorStatus: result.errorStatus } : {}),
        ...(result.errorCode ? { errorCode: result.errorCode } : {}),
      })
    })

    return () => {
      cancelled = true
    }
  }, [shadowReadEnabled, status, authPair, options.appointmentId])

  const markAsRead = useCallback(async () => {
    if (!conversationRef.current || messages.length === 0) return

    try {
      const last = messages[messages.length - 1]
      if (last.twilioMessageIndex == null) return
      await conversationRef.current.advanceLastReadMessageIndex(
        last.twilioMessageIndex
      )
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }, [messages])

  const notifyTyping = useCallback(() => {
    if (conversationRef.current) {
      conversationRef.current.typing()
    }
  }, [])

  const deleteMessage = useCallback(
    async (messageSid: string) => {
      if (!messageSid || deletingMessageSid) return
      const twilioMsg = twilioMessageBySidRef.current.get(messageSid)
      if (!twilioMsg || typeof twilioMsg.remove !== 'function') {
        alertDanger('Não foi possível apagar a mensagem.')
        return
      }
      setDeletingMessageSid(messageSid)
      try {
        await twilioMsg.remove()
        dropMessageFromChatState(messageSid)
      } catch (error) {
        console.error('Error deleting message:', error)
        alertDanger('Não foi possível apagar a mensagem.')
      } finally {
        setDeletingMessageSid(null)
      }
    },
    [deletingMessageSid, dropMessageFromChatState]
  )

  const sendText = useCallback(async (text: string, files?: File[]) => {
    if (!conversationRef.current) return
    const trimmed = text.trim()
    const hasFiles = Boolean(files && files.length > 0)
    if (!trimmed && !hasFiles) return
    if (sendInFlightRef.current) return
    sendInFlightRef.current = true
    setSending(true)
    try {
      setErrorMessage(null)
      const builder = conversationRef.current.prepareMessage()
      if (trimmed) {
        builder.setBody(trimmed)
      }

      if (files && files.length > 0) {
        for (const file of files) {
          const prepared = await prepareFileForTwilioSend(file)
          builder.addMedia(fileToSendMediaOptions(prepared))
        }
      }

      const message = await builder.build().send()
      await safeAdvanceLastReadMessageIndex(
        conversationRef.current,
        message.index
      )
      chatAnalytics.trackMessageSent({
        appointmentId: options.appointmentId,
        doctorId: options.professionalId,
      })
    } catch (error) {
      console.error('Error sending message:', error)
      const msg = formatChatSendError(error, 'text')
      alertDanger(msg)
      setErrorMessage(null)
      chatAnalytics.trackMessageFailed({
        appointmentId: options.appointmentId,
        doctorId: options.professionalId,
        reason: error instanceof Error ? error.message : 'unknown',
      })
    } finally {
      sendInFlightRef.current = false
      setSending(false)
    }
  }, [])

  const sendMedia = useCallback(async (file: File) => {
    if (!conversationRef.current) return
    if (sendInFlightRef.current) return
    sendInFlightRef.current = true
    setSending(true)
    try {
      setErrorMessage(null)
      const prepared = await prepareFileForTwilioSend(file)
      const builder = conversationRef.current.prepareMessage()
      builder.addMedia(fileToSendMediaOptions(prepared))

      const message = await builder.build().send()
      await safeAdvanceLastReadMessageIndex(
        conversationRef.current,
        message.index
      )
    } catch (error) {
      console.error('Error sending media:', error)
      const msg = formatChatSendError(error, 'media')
      alertDanger(msg)
      setErrorMessage(null)
    } finally {
      sendInFlightRef.current = false
      setSending(false)
    }
  }, [])

  const sendAttachments = useCallback(
    async (files: File[], caption?: string) => {
      if (!conversationRef.current || files.length === 0) return
      if (sendInFlightRef.current) return
      sendInFlightRef.current = true
      setSending(true)
      try {
        setErrorMessage(null)
        const trimmed = caption?.trim() ?? ''
        for (let i = 0; i < files.length; i++) {
          const file = files[i]!
          const prepared = await prepareFileForTwilioSend(file)
          const builder = conversationRef.current.prepareMessage()
          if (i === 0 && trimmed) {
            builder.setBody(trimmed)
          }
          builder.addMedia(fileToSendMediaOptions(prepared))
          const message = await builder.build().send()
          await safeAdvanceLastReadMessageIndex(
            conversationRef.current,
            message.index
          )
        }
        chatAnalytics.trackMessageSent({
          appointmentId: options.appointmentId,
          doctorId: options.professionalId,
        })
      } catch (error) {
        console.error('Error sending attachments:', error)
        const msg = formatChatSendError(error, 'media')
        alertDanger(msg)
        setErrorMessage(null)
        chatAnalytics.trackMessageFailed({
          appointmentId: options.appointmentId,
          doctorId: options.professionalId,
          reason: error instanceof Error ? error.message : 'unknown',
        })
      } finally {
        sendInFlightRef.current = false
        setSending(false)
      }
    },
    [options.appointmentId, options.professionalId]
  )

  useEffect(() => {
    // Sem token, ids do par nem o `authPair`: o console aparece em screen share
    // e em ferramentas de captura de erro.
    if (!enabled || !authPair) {
      console.debug('[TwilioChat] guard blocked', {
        enabled,
        hasAuthPair: Boolean(authPair),
      })
      return
    }

    let isMounted = true

    /**
     * Conversa fechada, confirmada por `estaEncerrada` (lista) ou pelo 409
     * `CONVERSATION_CLOSED` do `resumeOrReject` (criação). Nos dois casos o
     * backend já decidiu — sem reconsulta, sem retry — então aplica o mesmo
     * estado que `garantirPresenca()` usa para o 403 de encerramento: histórico
     * legível, escrita fechada, "Reabrir conversa" quando a flag permitir.
     *
     * Não depende de `conversation_close_read` de propósito: sem este desvio o
     * 409 cai em `status: 'error'` (o bug), e as flags chegam do Remote Config
     * depois do primeiro render, então gatear aqui reintroduziria a falha na
     * primeira carga. Só o rótulo por evento do SDK (`handleParticipationLost`)
     * segue atrás da flag.
     */
    const aplicarFechada = () => {
      const d: PresenceDiagnosis = { kind: 'closed' }
      precisaReopenRef.current = true
      setPresenceMessage(presenceMessageFor(d))
      // Sem o id interno (409 sem match na lista) o `reopen` não tem alvo:
      // oferecer o botão seria prometer o que não se cumpre.
      // A flag (`closeAction`) é aplicada no retorno do hook, onde acompanha o
      // Remote Config mesmo quando ele resolve depois deste ponto.
      setCanReopen(podeTentarReabrir(d) && !!conversationInternalIdRef.current)
      setStatus('readOnly')
    }

    /**
     * Fase 4 — garante a participação antes de conectar.
     *
     * Devolve `true` quando a escrita está liberada. Falha aqui **nunca** vira
     * erro de tela: o histórico continua legível e a conversa entra em
     * somente-leitura, com "Reabrir conversa" quando fizer sentido tentar.
     *
     * A spec põe este passo entre a resolução da conversa e o token; aqui ele
     * roda depois do token e antes de conectar o Client, porque é essa a ordem
     * do fluxo existente. O que importa — participação garantida antes de
     * escrever — se mantém.
     */
    const garantirPresenca = async (): Promise<boolean> => {
      const id = conversationInternalIdRef.current
      if (!presenceEnsureEnabled || !id) return true

      try {
        const r = await postPresenceEnsure(id, authPair)
        if (r.professionalPresence === 'ACTIVE') {
          setPresenceMessage(null)
          setCanReopen(false)
          return true
        }
        // 200 sem presença ativa: o backend aceitou o pedido mas não colocou o
        // profissional na conversa. Tratar como falta de vaga, não como erro.
        const d: PresenceDiagnosis = { kind: 'capacity' }
        setPresenceMessage(presenceMessageFor(d))
        setCanReopen(true)
        return false
      } catch (err) {
        const typed = toConversationsApiError(err)
        const code =
          typed instanceof ConversationsApiError ? typed.code : undefined
        const d = diagnosePresence403(code, tokenIdentityRef.current, {
          participants: conversationParticipantsRef.current,
        })
        precisaReopenRef.current = exigeReopen(d)
        setPresenceMessage(presenceMessageFor(d))
        // Conversa encerrada só reabre com a flag de ação: sem ela, oferecer o
        // botão seria prometer o que não se cumpre.
        setCanReopen(
          podeTentarReabrir(d) &&
            (!exigeReopen(d) || closeFlagsRef.current.closeAction)
        )
        return false
      }
    }

    const initializeChat = async () => {
      setStatus('connecting')
      setErrorMessage(null)
      // Re-init (reabertura por fora, token expirado): estado do run anterior
      // não pode sobreviver — `aplicarFechada` deixa `precisaReopenRef` ligado e
      // a mensagem de encerrada na tela.
      precisaReopenRef.current = false
      conversationParticipantsRef.current = []
      setPresenceMessage(null)
      setCanReopen(false)
      seenSidsRef.current.clear()
      twilioMessageBySidRef.current.clear()

      try {
        let conversationSid = initialConversationSid

        const tokenResponse = await postConversationsToken(twilioAs, authPair)
        tokenIdentityRef.current = (
          tokenResponse as { identity?: string }
        ).identity

        if (!conversationSid) {
          try {
            const existing = await getConversationsList(authPair, {
              person_id: personIdStr!,
              professional_id: professionalIdStr!,
            })
            const match = acharConversaDoPar(
              existing,
              personIdStr!,
              professionalIdStr!
            )
            chatAnalytics.trackHistoryLoaded({
              appointmentId: options.appointmentId,
              doctorId: options.professionalId,
              messagesCount: existing.length,
            })
            if (match?.id) {
              conversationInternalIdRef.current = String(match.id)
            }
            if (Array.isArray(match?.participants)) {
              conversationParticipantsRef.current = match.participants
            }
            if (match && estaEncerrada(match)) {
              // A lista já devolveu a conversa com `closedAt` preenchido: nem
              // vale chamar `postConversations` (o `resumeOrReject` do backend
              // rejeitaria do mesmo jeito) nem tocar no SDK. Mesma checagem que
              // o app do paciente faz via `getConversationByPair` antes do SDK.
              if (isMounted) aplicarFechada()
              return
            }
            if (match?.twilioConversationSid) {
              conversationSid = match.twilioConversationSid
            }
          } catch (e) {
            console.debug(
              '[TwilioChat] getConversationsList failed',
              describeConversationsError(toConversationsApiError(e))
            )
          }
        }

        if (!conversationSid) {
          const convResponse = await postConversations(
            { personId: personIdStr!, professionalId: professionalIdStr! },
            authPair
          )
          if (convResponse.id) {
            conversationInternalIdRef.current = String(convResponse.id)
          }
          conversationSid =
            convResponse.twilioConversationSid || convResponse.id
        }
        const escritaLiberada = await garantirPresenca()

        const { Client } = await import('@twilio/conversations')
        const client = new Client(tokenResponse.token)

        clientRef.current = client

        // SDK passa a string do estado (ex. connected), não { connectionState }
        client.on('connectionStateChanged', (newState: string) => {
          if (isMounted) {
            setConnectionState(newState as ConnectionState)
          }
        })

        client.on('tokenAboutToExpire', async () => {
          try {
            const newTokenResponse = await postConversationsTokenRefresh(
              twilioAs,
              authPair
            )
            await client.updateToken(newTokenResponse.token)
          } catch (error) {
            console.error('Failed to refresh token:', error)
          }
        })

        client.on('tokenExpired', () => {
          clientIterationRef.current += 1
        })

        client.on('initFailed', ({ error }) => {
          // Mensagem do SDK vai para o log, não para a tela: o texto de timeout
          // do Twilio cita nome de credencial de infraestrutura.
          const detail = error as unknown as {
            code?: unknown
            status?: unknown
          }
          console.error('[TwilioChat] initFailed', {
            code: detail?.code,
            status: detail?.status,
          })
          if (isMounted) {
            setErrorMessage(null)
            setStatus('error')
          }
        })

        // getConversationBySid já aguarda _ensureReady (twilsock + bootstrap)
        const conversation = await client.getConversationBySid(conversationSid!)
        conversationRef.current = conversation

        /**
         * Presença por demanda: o backend libera a participação do profissional
         * em conversas frias para não estourar a cota de 1000 por identidade.
         * Sem assinar estes três eventos, a participação sai com o chat aberto,
         * o SDK avisa, ninguém escuta, e a UI congela mostrando um campo de
         * escrita que não funciona mais. Ausência de participação é estado
         * esperado do modelo novo, não erro.
         */
        const handleParticipationLost = () => {
          if (!isMounted) return
          setStatus('readOnly')

          /**
           * Fase 5 — a saída de participação tem **duas causas**: encerramento
           * (`closedAt`) e liberação de quota do profissional (presença por
           * demanda). Só a API distingue.
           *
           * Rotular pelo evento do SDK diria "encerrado" quando foi só o job de
           * quota liberando a vaga — e nesse caso basta `presence/ensure` para
           * voltar a escrever. Por isso reconsulta antes de nomear o estado.
           *
           * A tela já entrou em `readOnly` acima: a reconsulta só refina a
           * mensagem, nunca atrasa o fechamento da escrita.
           */
          // Passos 1–3 (defensivos): sem `conversation_close_read` a tela fica
          // no `readOnly` genérico, que é o comportamento anterior à Fase 5.
          const id = conversationInternalIdRef.current
          if (!id || !authPair || !closeFlagsRef.current.closeRead) return

          void getConversationById(id, authPair)
            .then((conversa) => {
              if (!isMounted) return
              if (estaEncerrada(conversa)) {
                const d: PresenceDiagnosis = { kind: 'closed' }
                precisaReopenRef.current = true
                setPresenceMessage(presenceMessageFor(d))
                setCanReopen(podeTentarReabrir(d))
              }
              // Não encerrada: é quota. O `readOnly` já está aplicado e o botão
              // de reabrir segue valendo com `presence/ensure` sozinho.
            })
            .catch(() => {
              // Medição de estado não pode derrubar a tela: fica no `readOnly`
              // genérico, que já é o comportamento seguro.
            })
        }

        conversation.on('removed', handleParticipationLost)
        client.on('conversationRemoved', (c: any) => {
          if (c?.sid === conversation.sid) handleParticipationLost()
        })
        client.on('conversationLeft', (c: any) => {
          if (c?.sid === conversation.sid) handleParticipationLost()
        })

        /**
         * Fase 3: com `chat_history_as_source` ligada, o histórico vem do
         * `/history` e o SDK fica só com escrita, tempo real e typing.
         *
         * Se a leitura pela API falhar, cair para o SDK em vez de deixar o
         * profissional sem histórico: é dado clínico, e a fonte antiga
         * continua correta enquanto a nova não provou paridade.
         */
        let carregouPelaApi = false

        if (historyAsSource && authPair) {
          try {
            const page = await getConversationsHistory(
              {
                personId: authPair.personId,
                professionalId: authPair.professionalId,
                limit: 30,
                order: 'desc',
              },
              authPair
            )
            const iniciais = page.items
              .filter(historyMessageHasContent)
              .map(mapHistoryMessage)

            setMessages(mergeBySid([], iniciais) as never)
            iniciais.forEach((m) => seenSidsRef.current.add(m.sid))

            historyCursorRef.current = page.nextCursor ?? null
            setHasMoreMessages(Boolean(page.nextCursor))

            // `unread.professional` da API passa a alimentar o badge.
            if (typeof page.unread?.professional === 'number') {
              setUnreadCount(page.unread.professional)
            }
            carregouPelaApi = true
          } catch (e) {
            console.warn(
              '[TwilioChat] /history indisponível; usando o SDK como fonte',
              e
            )
          }
        }

        const messagesPaginator = await conversation.getMessages(30)

        if (!carregouPelaApi) {
          const rawInOrder = messagesPaginator.items.filter((m: any) =>
            hasRenderableTwilioContent(m)
          )
          const initialMessages = sortMessagesByConversationOrder(
            await Promise.all(
              rawInOrder.map(async (m: any) => {
                rememberTwilioMessage(m)
                const mapped = mapTwilioMessage(m)
                return enrichMessageWithMediaDownloadUrl(m, mapped)
              })
            )
          )

          setMessages(initialMessages)
          initialMessages.forEach((m: ServiceRoomChatMessage) => {
            seenSidsRef.current.add(m.sid)
          })

          setHasMoreMessages(!!messagesPaginator.hasPrevPage)
        } else {
          // O paginator ainda é guardado: `rememberTwilioMessage` mantém o
          // objeto do SDK das mensagens da janela recente, que é o que resolve
          // URL de anexo para o que chegou ao vivo.
          messagesPaginator.items.forEach((m: any) => rememberTwilioMessage(m))
        }

        paginatorRef.current = messagesPaginator as never

        // Já abrir em somente-leitura se a participação não existe mais. O
        // histórico segue legível; só a escrita fecha.
        if (conversation.status === 'notParticipating') {
          handleParticipationLost()
        }

        conversation.on('messageAdded', (message: any) => {
          rememberTwilioMessage(message)
          if (seenSidsRef.current.has(message.sid)) return
          if (!hasRenderableTwilioContent(message)) return
          const mapped = mapTwilioMessage(message)
          seenSidsRef.current.add(message.sid)
          setMessages((prev) => {
            if (prev.some((m) => m.sid === mapped.sid)) {
              return prev
            }
            return sortMessagesByConversationOrder([...prev, mapped])
          })
          chatAnalytics.trackMessageReceived({
            appointmentId: options.appointmentId,
            doctorId: options.professionalId,
          })
          if (
            isIncomingUnreadForViewer(
              message.author,
              twilioAs,
              personIdStr,
              professionalIdStr
            )
          ) {
            setUnreadCount((prev) => prev + 1)
          }
          void enrichMessageWithMediaDownloadUrl(message, mapped).then(
            (enriched) => {
              if (!enriched.attributes?.fileUrl) {
                return
              }
              if (!isMounted) {
                return
              }
              setMessages((prev) => {
                const i = prev.findIndex((m) => m.sid === enriched.sid)
                if (i === -1) {
                  return prev
                }
                const next = [...prev]
                next[i] = enriched
                return sortMessagesByConversationOrder(next)
              })
            }
          )
        })

        conversation.on('messageRemoved', (message: any) => {
          const sid = message?.sid
          if (typeof sid !== 'string' || !sid) return
          dropMessageFromChatState(sid)
        })

        conversation.on(
          'messageUpdated',
          ({
            message,
          }: {
            message: any
            updateReasons: MessageUpdateReason[]
          }) => {
            rememberTwilioMessage(message)
            if (!hasRenderableTwilioContent(message)) {
              dropMessageFromChatState(message.sid)
              return
            }
            const pushOrReplace = (mapped: ServiceRoomChatMessage) => {
              setMessages((prev) => {
                const i = prev.findIndex((m) => m.sid === message.sid)
                if (i === -1) {
                  if (prev.some((m) => m.sid === mapped.sid)) return prev
                  seenSidsRef.current.add(message.sid)
                  return sortMessagesByConversationOrder([...prev, mapped])
                }
                const next = [...prev]
                next[i] = mapped
                return sortMessagesByConversationOrder(next)
              })
            }
            const mapped = mapTwilioMessage(message)
            pushOrReplace(mapped)
            void enrichMessageWithMediaDownloadUrl(message, mapped).then(
              (enriched) => {
                if (!enriched.attributes?.fileUrl) {
                  return
                }
                if (!isMounted) {
                  return
                }
                pushOrReplace(enriched)
              }
            )
          }
        )

        conversation.on('typingStarted', (participant: any) => {
          setTypingParticipants((prev) => {
            if (prev.includes(participant.identity)) return prev
            return [...prev, participant.identity]
          })
        })

        conversation.on('typingEnded', (participant: any) => {
          setTypingParticipants((prev) =>
            prev.filter((id) => id !== participant.identity)
          )
        })

        /**
         * Fase 3: `POST /:id/read` com o UUID interno substitui o
         * `advanceLastReadMessageIndex` quando o `/history` é a fonte — o
         * `unread.professional` da API é calculado a partir dele.
         */
        if (carregouPelaApi && conversationInternalIdRef.current && authPair) {
          try {
            await markConversationRead(
              conversationInternalIdRef.current,
              authPair
            )
          } catch (e) {
            // Não impede o uso do chat: só o badge fica desatualizado.
            console.warn('[TwilioChat] POST /read falhou', e)
          }
        } else {
          const ultima = messagesPaginator.items[
            messagesPaginator.items.length - 1
          ] as { index?: number } | undefined
          if (ultima?.index != null) {
            await safeAdvanceLastReadMessageIndex(conversation, ultima.index)
          }
        }
        setUnreadCount(0)

        if (isMounted) {
          // Sem vaga para participar: histórico legível, escrita fechada.
          setStatus(escritaLiberada ? 'ready' : 'readOnly')
        }
      } catch (error) {
        const typed = toConversationsApiError(error)

        // Allowlist: conteúdo de chat é dado clínico, então nem o corpo da
        // resposta nem a mensagem do servidor entram em log.
        console.error(
          '[TwilioChat] falha ao inicializar',
          describeConversationsError(typed)
        )

        if (isMounted) {
          const closed =
            typed instanceof ConversationsApiError && typed.isClosed

          // `postConversations` pode achar a conversa já fechada antes de o SDK
          // entrar em cena (`resumeOrReject` do backend, 409). Sem este desvio,
          // isto caía no `status: 'error'` genérico — sem `presenceMessage` nem
          // "Reabrir conversa" — mesmo sendo o mesmo estado que
          // `garantirPresenca()` já sabe tratar quando o 403 vem depois.
          if (closed) {
            aplicarFechada()
            return
          }

          const capacity =
            typed instanceof ConversationsApiError && typed.isCapacity

          // A `message` do servidor é volátil por contrato e pode carregar
          // detalhe de infraestrutura. A tela usa texto fixo; o servidor fica
          // no log.
          setErrorMessage(null)
          setRetryAfterSeconds(
            capacity && typed instanceof ConversationsApiError
              ? (typed.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS)
              : null
          )
          setStatus(capacity ? 'unavailable' : 'error')
        }
      }
    }

    initializeChat()

    return () => {
      isMounted = false

      if (conversationRef.current) {
        conversationRef.current.removeAllListeners()
      }

      if (clientRef.current) {
        clientRef.current.removeAllListeners()
        clientRef.current.shutdown()
      }

      clientRef.current = null
      conversationRef.current = null
      twilioMessageBySidRef.current.clear()
    }
  }, [
    enabled,
    personIdStr,
    professionalIdStr,
    twilioAs,
    initialConversationSid,
    clientIterationRef.current,
    reinitKey,
    rememberTwilioMessage,
    dropMessageFromChatState,
  ])

  /**
   * Fase 4 — "Reabrir conversa".
   *
   * É o que transforma o estado terminal em algo que o profissional resolve
   * sozinho quando a vaga volta. Só o `presence/ensure` é refeito: o histórico
   * já está na tela e não precisa ser recarregado.
   *
   * Não faz retry automático de propósito — insistir sozinho contra um serviço
   * sem capacidade é o padrão que originou esta migração.
   */
  const reopenConversation = useCallback(async () => {
    const id = conversationInternalIdRef.current
    if (!id || !authPair || reopening) return

    setReopening(true)
    try {
      /**
       * Fase 5: conversa encerrada exige `reopen` **antes** do `ensure`. O
       * `reopen` readmite o paciente e limpa `closedAt`, mas **não** readmite o
       * profissional — a presença dele segue por demanda. Chamar só o `ensure`
       * numa conversa encerrada devolve 409 para sempre, e o botão nunca
       * funcionaria.
       */
      if (precisaReopenRef.current) {
        // Passos 5–6: sem `conversation_close_action` não há como reabrir, e o
        // botão nem deveria estar visível (ver `setCanReopen` abaixo).
        if (!closeFlags.closeAction) return
        await postConversationReopen(id, authPair)
        precisaReopenRef.current = false
      }

      const r = await postPresenceEnsure(id, authPair)
      if (r.professionalPresence === 'ACTIVE') {
        setPresenceMessage(null)
        setCanReopen(false)
        // Conversa fechada na entrada (`aplicarFechada`) nunca criou o Client:
        // `ready` sozinho liberaria o input sem cliente e `sendText` descartaria
        // a mensagem em silêncio. Sem cliente, reinicia o fluxo completo.
        if (conversationRef.current) setStatus('ready')
        else reinitialize()
        return
      }
      const d: PresenceDiagnosis = { kind: 'capacity' }
      setPresenceMessage(presenceMessageFor(d))
      setCanReopen(true)
    } catch (err) {
      const typed = toConversationsApiError(err)
      const code =
        typed instanceof ConversationsApiError ? typed.code : undefined
      const d = diagnosePresence403(code, tokenIdentityRef.current, {
        participants: conversationParticipantsRef.current,
      })
      precisaReopenRef.current = exigeReopen(d)
      setPresenceMessage(presenceMessageFor(d))
      setCanReopen(podeTentarReabrir(d))
    } finally {
      setReopening(false)
    }
  }, [authPair, reopening, closeFlags.closeAction, reinitialize])

  return {
    status,
    retryAfterSeconds,
    /** Fase 4: motivo de a escrita estar fechada; `null` quando liberada. */
    presenceMessage,
    /** `false` quando reabrir não resolveria (ex.: namespace divergente). */
    canReopen:
      canReopen && (!precisaReopenRef.current || closeFlags.closeAction),
    reopenConversation,
    /** Re-executa a inicialização do chat (ex.: conversa reaberta por fora). */
    reinitialize,
    reopening,
    connectionState,
    errorMessage,
    messages,
    sendText,
    sendMedia,
    sendAttachments,
    sending,
    typingParticipants,
    notifyTyping,
    markAsRead,
    unreadCount,
    hasMoreMessages,
    loadingMore,
    loadMoreMessages,
    getFreshMediaDownloadUrl,
    deleteMessage,
    deletingMessageSid,
  }
}
